import {useEffect, useRef, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import {ArrowLeftIcon, ChevronLeftIcon, ChevronRightIcon} from "lucide-react";
import TaskActionButtons from "components/taskdetails-remake/navigation/TaskActionButtons.component";
import * as reduxSelectors from "components/store/application.reducers";
import {useTranslation} from "components/translations/useTranslation";
import {Button, ButtonType} from "components/common/Button.component";
import {ICON_SIZE} from "components/common/constants";
import {Tooltip} from "components/tooltip/Tooltip";
import {SHARED_TOOLTIP_ID, TRUNCATION_TOLERANCE_PX} from "components/tooltip/AppTooltip.component";
import {DOCUMENT_TYPE, LOADING_STATUS} from "utils/constants";
import type {TaskHeaderProps} from "types/taskHeader";
import "../style/taskHeader.scss";

const TITLE_TOOLTIP_ID = "task-header-title-tooltip";
// how long the previous / next buttons stay disabled before moving, so a double click moves one task
const NAVIGATION_DELAY = 500;

/**
 * The task header of the redesigned task details page: back to the list, the document title and
 * where it came from on the left; the task actions and the move between tasks on the right.
 * It replaces the classic Subnavbar (see taskdetails-remake/navigation/Subnavbar.component).
 */
export const TaskHeader = (props: TaskHeaderProps) => {
    const {translate, translationKeyExists} = useTranslation();
    const navigate = useNavigate();
    const titleRef = useRef<HTMLHeadingElement>(null);
    const navigationTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
    const [isNavigating, setIsNavigating] = useState(false);

    const userSettings = useSelector(reduxSelectors.getUsersSettings);
    const documentDataLoading = useSelector(reduxSelectors.getTaskDocumentDataLoadingStatus);
    const workflowDetailsLoading = useSelector(reduxSelectors.getWorkflowDetailsLoadingStatus);
    const commentsLoading = useSelector(reduxSelectors.getTaskDraftCommentsLoadingStatus);
    const documentPreviewLoading = useSelector(reduxSelectors.getAttachmentsLoadingStatus);
    const isTaskDetailsLocked = useSelector(reduxSelectors.getIsTaskDetailsLocked);
    const hasDraftComment = useSelector(reduxSelectors.getTaskDraftCommentText);
    const tasksWaitingWSUpdate = useSelector(reduxSelectors.getTasksWaitingWSUpdate);

    const {task, taskDetails, navigation, params} = props;

    // the page stays mounted between tasks, so the buttons come back once the next one is shown
    useEffect(() => {
        setIsNavigating(false);
    }, [params.taskId]);

    useEffect(() => () => clearTimeout(navigationTimeoutRef.current), []);

    const moveToTask = (isForward: boolean) => {
        setIsNavigating(true);
        navigationTimeoutRef.current = setTimeout(() => {
            setIsNavigating(false);
            // the uid route navigates by itself and returns no path
            const nextTaskPath = props.navigateBetweenTasks(params.taskId, isForward);
            if (nextTaskPath) navigate(nextTaskPath, {replace: true});
        }, NAVIGATION_DELAY);
    };

    const taskWaitingWSUpdate = tasksWaitingWSUpdate?.includes(params.taskId);

    const taskActionButtonsProps = {
        task,
        taskDetails,
        actions: props.activeActions,
        selectedTaskKeys: [params.taskId],
        action: props.taskAction,
        translate,
        userSettings,
        // the external editor is a module on the page, so there is no popup to open: the classic
        // Subnavbar only offered one when the editor was available and had no tab, which never holds
        openEditorPopup: false,
        backToMyTasks: props.navigateBack,
        isReadOnly: props.editorReadOnly,
        disableAll: isTaskDetailsLocked || taskWaitingWSUpdate || documentDataLoading || workflowDetailsLoading || commentsLoading || documentPreviewLoading === LOADING_STATUS.LOADING,
        docType: props.type,
        documentVersion: taskDetails?.documentVersion,
        isHandled: task.isHandled,
        hasDraftComment,
    };
    const documentTypeLabel = translate("documentType." + DOCUMENT_TYPE.asString(task.documentType));
    const hasDocumentContext = Boolean(navigation.shortTitle && navigation.companyName);

    // The whole title as one string: the tooltip stands in for the title that did not fit, so it
    // has to repeat all of it and not just the fragment that got cut off.
    const fullTitle = hasDocumentContext
        ? `${documentTypeLabel} - ${navigation.shortTitle} → ${navigation.companyName}`
        : documentTypeLabel;

    // measured on hover, so a title that fits returns null and react-tooltip skips the empty tip
    const getTitleTooltipContent = () => {
        const title = titleRef.current;
        if (!title || title.scrollWidth - title.clientWidth <= TRUNCATION_TOLERANCE_PX) {
            return null;
        }
        return fullTitle;
    };

    // spaces and dots are left out of the translation keys
    const applicationName = task.externalApplicationName || task.applicationTypeName || '';
    const externalApplicationNameKey = applicationName.replace(/[ .]/g, '');
    const applicationNameLabel = translationKeyExists(`taskDetail.applicationName.${externalApplicationNameKey}`)
        ? translate(`taskDetail.applicationName.${externalApplicationNameKey}`)
        : applicationName;

    return (
        <div className="task-details-header" data-cy="taskDetail.taskHeader">
            <div className="task-details-header__start">
                <Button type={ButtonType.GHOST}
                        iconOnly={true}
                        icon={<ArrowLeftIcon size={ICON_SIZE.MEDIUM}/>}
                        onClick={() => {
                            props.navigateBack();
                            navigate(props.navigateBackPath);
                        }}
                        dataCy="taskDetail.subnavbar.backToList"
                        ariaLabel={translate("taskDetail.backToList")}
                        tooltip={{id: "task-header-back-tooltip", content: () => translate("taskDetail.backToList")}}
                />

                <div className="task-details-header__titles">
                    <h1 className="task-details-header__title"
                        ref={titleRef}
                        data-tip={TITLE_TOOLTIP_ID}
                        data-for={SHARED_TOOLTIP_ID}
                        data-cy="taskDetail.subnavbar.title">
                        <strong>{documentTypeLabel}</strong>
                        {hasDocumentContext && ` - ${navigation.shortTitle} → ${navigation.companyName}`}
                    </h1>
                    <Tooltip id={TITLE_TOOLTIP_ID} content={getTitleTooltipContent}/>
                    <p className="task-details-header__subtitle" data-cy="taskDetail.subnavbar.subtitle">
                        {translate('taskDetail.received')}
                        {applicationName && ` ${translate('taskDetail.from')} ${applicationNameLabel}`}
                    </p>
                </div>
            </div>

            <div className="task-details-header__end">
                <div className={`task-details-header__actions${taskActionButtonsProps.disableAll ? ' disabled' : ''}`}>
                    <TaskActionButtons {...taskActionButtonsProps}/>
                </div>

                <div className="task-details-header__counter">
                    <span className="task-details-header__counter-label" data-cy="taskDetail.subnavbar.currentTask">
                        {`${translate('taskDetail.' + props.type + '.type')} ${navigation.currentTask} / ${navigation.total}`}
                    </span>
                    {/* disabled at the ends rather than hidden, so the controls do not move */}
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<ChevronLeftIcon size={ICON_SIZE.MEDIUM}/>}
                            onClick={() => moveToTask(false)}
                            disabled={isNavigating || navigation.currentTask === 1}
                            ariaLabel={translate("taskDetail.actionBar.moveToPrevious")}
                            dataCy="taskDetail.actionBar.moveToPrevious"
                            tooltip={{id: "task-header-prev-tooltip", content: () => translate("taskDetail.actionBar.moveToPrevious")}}
                    />
                    <Button type={ButtonType.GHOST}
                            iconOnly={true}
                            icon={<ChevronRightIcon size={ICON_SIZE.MEDIUM}/>}
                            onClick={() => moveToTask(true)}
                            disabled={isNavigating || navigation.currentTask === navigation.total}
                            ariaLabel={translate("taskDetail.actionBar.moveToNext")}
                            dataCy="taskDetail.actionBar.moveToNext"
                            tooltip={{id: "task-header-next-tooltip", content: () => translate("taskDetail.actionBar.moveToNext")}}
                    />
                </div>
            </div>
        </div>
    );
};

export default TaskHeader;
